import React, { useCallback, useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { Button, MediumTitle, useFlashMessage } from '@indigo-ag/component-module';
import { AgFundInfoInput, AgFinancingPhase4Status, AgGroup } from '@indigo-ag/schema';

import i18next from 'core/i18n';
import { EditableNumberField, GridContainer, GridContainerItem, PhaseSection, usePrompt, utils } from 'shared';
import { upsertFundInfo } from 'features/brazil-grower/queries';

interface FundInfoProps {
  group: AgGroup;
}

const isNotEmpty = (value?: number): boolean => {
  return value !== undefined && value !== null;
};

export const FundInfo: React.FunctionComponent<FundInfoProps> = ({ group }) => {
  const getInitialFundInfo = useCallback((): Partial<AgFundInfoInput> => {
    return {
      indigoEquityContribution: group.fundInfo?.indigoEquityContribution,
      indigoAssetPurchase: group.fundInfo?.indigoAssetPurchase,
      indigoCashConstitution: group.fundInfo?.indigoCashConstitution,
      indigoEquityContributionAdjustment: group.fundInfo?.indigoEquityContributionAdjustment,
      indigoFundCashEquivalent: group.fundInfo?.indigoFundCashEquivalent,

      fundAssetPurchase: group.fundInfo?.fundAssetPurchase,
      fundCashConstitution: group.fundInfo?.fundCashConstitution,
      fundEquityContribution: group.fundInfo?.fundEquityContribution,
      fundEquityContributionAdjustment: group.fundInfo?.fundEquityContributionAdjustment,

      assignmentAmount: group.fundInfo?.assignmentAmount,
      variation: group.fundInfo?.variation
    };
  }, [group.fundInfo]);

  /**
   * State
   */
  const [fundInfoUpdated, setFundInfoUpdated] = useState<Partial<AgFundInfoInput>>(getInitialFundInfo());
  const [isSaving, setIsSaving] = useState(false);
  const [isInformationFulledRight, setIsInformationFulledRight] = useState(false);
  const [isUpdatingInformation, setIsUpdatingInformation] = useState(false);

  /**
   * Mutations
   */
  const [upsertFundInfoFn] = useMutation(upsertFundInfo);

  const { addFlashMessage } = useFlashMessage();

  useEffect(() => {
    setIsInformationFulledRight(
      isNotEmpty(fundInfoUpdated.fundAssetPurchase) &&
        isNotEmpty(fundInfoUpdated.fundCashConstitution) &&
        isNotEmpty(fundInfoUpdated.fundEquityContribution) &&
        isNotEmpty(fundInfoUpdated.fundEquityContributionAdjustment) &&
        isNotEmpty(fundInfoUpdated.assignmentAmount) &&
        isNotEmpty(fundInfoUpdated.variation)
    );
  }, [fundInfoUpdated]);

  usePrompt(isUpdatingInformation);

  const saveFundInfo = () => {
    setIsSaving(true);

    upsertFundInfoFn({
      variables: {
        agGroupId: group.id,
        fundInfo: fundInfoUpdated
      }
    })
      .then(() => {
        setIsUpdatingInformation(false);
        addFlashMessage({
          content: utils.text.firstLetterUppercase(i18next.t('fundInfoUpdatedSuccessfully')),
          title: '',
          variant: 'success'
        });
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const updateGuaranteeNumberInfo = (prop: keyof Partial<AgFundInfoInput>) => {
    return (value: number | null): void => {
      const updatedGuaranteeData = {
        ...fundInfoUpdated,
        [prop]: value
      };
      setFundInfoUpdated(updatedGuaranteeData);
      setIsUpdatingInformation(true);
    };
  };

  const isReadOnly =
    group.status !== AgFinancingPhase4Status.RECEIVED_BY_FUND && group.status !== AgFinancingPhase4Status.DENIED;

  const gridItems: Record<'indigo' | 'fund' | 'other', GridContainerItem[]> = {
    indigo: [
      // indigoEquityContribution
      {
        customField: (
          <EditableNumberField
            label={`${i18next.t('equityContribution')} (${i18next.t('optional')})`}
            name="indigoEquityContribution"
            onNumberChange={updateGuaranteeNumberInfo('indigoEquityContribution')}
            numberValue={fundInfoUpdated.indigoEquityContribution}
            readonly={isReadOnly}
            nullable
            allowNegative
          />
        )
      },
      // indigoAssetPurchase
      {
        customField: (
          <EditableNumberField
            label={`${i18next.t('assetPurchase')} (${i18next.t('optional')})`}
            name="indigoAssetPurchase"
            onNumberChange={updateGuaranteeNumberInfo('indigoAssetPurchase')}
            numberValue={fundInfoUpdated.indigoAssetPurchase}
            readonly={isReadOnly}
            nullable
            allowNegative
          />
        )
      },
      // indigoCashConstitution
      {
        customField: (
          <EditableNumberField
            label={`${i18next.t('cashConstitution')} (${i18next.t('optional')})`}
            name="indigoCashConstitution"
            onNumberChange={updateGuaranteeNumberInfo('indigoCashConstitution')}
            numberValue={fundInfoUpdated.indigoCashConstitution}
            readonly={isReadOnly}
            nullable
            allowNegative
          />
        )
      },
      // indigoEquityContributionAdjustment
      {
        customField: (
          <EditableNumberField
            label={`${i18next.t('equityContributionAdjustment')} (${i18next.t('optional')})`}
            name="indigoEquityContributionAdjustment"
            onNumberChange={updateGuaranteeNumberInfo('indigoEquityContributionAdjustment')}
            numberValue={fundInfoUpdated.indigoEquityContributionAdjustment}
            readonly={isReadOnly}
            nullable
            allowNegative
          />
        )
      },
      // indigoFundCashEquivalent
      {
        customField: (
          <EditableNumberField
            label={`${i18next.t('fundCashEquivalent')} (${i18next.t('optional')})`}
            name="indigoFundCashEquivalent"
            onNumberChange={updateGuaranteeNumberInfo('indigoFundCashEquivalent')}
            numberValue={fundInfoUpdated.indigoFundCashEquivalent}
            readonly={isReadOnly}
            nullable
            allowNegative
          />
        )
      }
    ],
    fund: [
      // fundEquityContribution
      {
        customField: (
          <EditableNumberField
            label={i18next.t('equityContribution')}
            name="fundEquityContribution"
            onNumberChange={updateGuaranteeNumberInfo('fundEquityContribution')}
            numberValue={fundInfoUpdated.fundEquityContribution}
            readonly={isReadOnly}
            nullable
            allowNegative
          />
        )
      },
      // fundAssetPurchase
      {
        customField: (
          <EditableNumberField
            label={i18next.t('assetPurchase')}
            name="fundAssetPurchase"
            onNumberChange={updateGuaranteeNumberInfo('fundAssetPurchase')}
            numberValue={fundInfoUpdated.fundAssetPurchase}
            readonly={isReadOnly}
            nullable
            allowNegative
          />
        )
      },
      // fundCashConstitution
      {
        customField: (
          <EditableNumberField
            label={i18next.t('cashConstitution')}
            name="fundCashConstitution"
            onNumberChange={updateGuaranteeNumberInfo('fundCashConstitution')}
            numberValue={fundInfoUpdated.fundCashConstitution}
            readonly={isReadOnly}
            nullable
            allowNegative
          />
        )
      },
      // fundEquityContributionAdjustment
      {
        customField: (
          <EditableNumberField
            label={i18next.t('equityContributionAdjustment')}
            name="fundEquityContributionAdjustment"
            onNumberChange={updateGuaranteeNumberInfo('fundEquityContributionAdjustment')}
            numberValue={fundInfoUpdated.fundEquityContributionAdjustment}
            readonly={isReadOnly}
            nullable
            allowNegative
          />
        )
      }
    ],
    other: [
      // assignmentAmount
      {
        customField: (
          <EditableNumberField
            label={i18next.t('assignmentAmount')}
            name="assignmentAmount"
            onNumberChange={updateGuaranteeNumberInfo('assignmentAmount')}
            numberValue={fundInfoUpdated.assignmentAmount}
            readonly={isReadOnly}
            nullable
            allowNegative
          />
        )
      },
      // variation
      {
        customField: (
          <EditableNumberField
            label={i18next.t('variation')}
            name="variation"
            onNumberChange={updateGuaranteeNumberInfo('variation')}
            numberValue={fundInfoUpdated.variation}
            readonly={isReadOnly}
            nullable
            allowNegative
          />
        )
      }
    ]
  };

  return (
    <PhaseSection title={i18next.t('fundInfo')}>
      <MediumTitle className="mb-2 mt-3 text-transform-uppercase-first-letter">Indigo</MediumTitle>
      <GridContainer columns={3} items={gridItems.indigo} />

      <MediumTitle className="mb-2 mt-3 text-transform-uppercase-first-letter">{i18next.t('fund')}</MediumTitle>
      <GridContainer columns={3} items={gridItems.fund} />

      <MediumTitle className="mb-2 mt-3 text-transform-uppercase-first-letter">{i18next.t('other')}</MediumTitle>
      <GridContainer columns={2} items={gridItems.other} />

      {group.status === AgFinancingPhase4Status.RECEIVED_BY_FUND || group.status === AgFinancingPhase4Status.DENIED ? (
        <div className="flex justify-flex-end">
          <Button
            onClick={saveFundInfo}
            type="submit"
            size="small"
            busy={isSaving}
            disabled={!isInformationFulledRight}
          >
            {i18next.t('save')}
          </Button>
        </div>
      ) : null}
    </PhaseSection>
  );
};
